import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Globe, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill } from '../componentes/ui/Pill.js';
import { SecaoRollover } from '../componentes/SecaoRollover.js';
import { useSessao } from '../auth/SessaoContexto.js';

/**
 * Domínios da operadora, no portal do cliente.
 *
 * O cliente vê os domínios que a equipe da Consult cadastrou e associou a ele, e
 * gerencia os registros deles. Não cria nem apaga domínio, nem mexe em DNSSEC —
 * isso implica criar zona e coordenar a delegação, e continua sendo do time
 * interno. Quem tem só leitura (papéis Solicitante e Leitura) enxerga os
 * registros, mas sem os botões de edição.
 *
 * Todos os endpoints são `/portal/dominios/*`, escopados por tenant no servidor:
 * mandar um id de domínio de outra operadora volta 403/404, não os dados.
 */

const TIPOS = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV'] as const;

interface Dominio {
  id: string;
  nome: string;
  operadora: { nome: string };
}

interface Registro {
  nome: string;
  tipo: string;
  ttl: number;
  valores: string[];
  sistema: boolean;
}

export function DominiosPortal() {
  const [aberto, setAberto] = useState<Dominio | null>(null);

  const { data: dominios = [], isLoading } = useQuery({
    queryKey: ['portal', 'dominios'],
    queryFn: () => api.get<Dominio[]>('/portal/dominios'),
  });

  return (
    <div className="space-y-5">
      <p className="text-[12.5px] text-texto-suave">
        Os domínios que a Consult cadastrou para a sua operadora. Aqui você gerencia os registros —
        o DNS "para frente" (site, e-mail, subdomínios). O reverso dos seus blocos fica na aba{' '}
        <strong>Meus IPs</strong>.
      </p>

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-card bg-borda-tenue" />
      ) : dominios.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie p-8 text-center">
          <Globe size={28} className="mx-auto text-texto-mudo" />
          <p className="mt-3 text-[13.5px] font-semibold">Nenhum domínio associado ainda</p>
          <p className="mt-1 text-[12.5px] text-texto-suave">
            Quando a Consult cadastrar um domínio para a sua operadora, ele aparece aqui para você
            gerenciar os registros.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <table className="w-full border-collapse text-[13.5px]">
            <tbody>
              {dominios.map((d) => (
                <tr key={d.id} className="border-b border-borda-suave last:border-0 hover:bg-borda-tenue">
                  <td className="px-4 py-3">
                    <span className="grid size-9 place-items-center rounded-chip bg-info-fundo text-info">
                      <Globe size={17} />
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <p className="font-mono font-semibold">{d.nome}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Botao tamanho="tabela" onClick={() => setAberto(d)}>
                      Gerenciar registros
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aberto && <ModalRegistros dominio={aberto} aoFechar={() => setAberto(null)} />}
    </div>
  );
}

function ModalRegistros({ dominio, aoFechar }: { dominio: Dominio; aoFechar: () => void }) {
  const qc = useQueryClient();
  const { pode } = useSessao();
  const podeEditar = pode('dominio:registro-escrever');
  const [form, setForm] = useState<{ nome: string; tipo: string; ttl: number; valores: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['portal', 'dominios', dominio.id, 'registros'],
    queryFn: () => api.get<{ registros: Registro[] }>(`/portal/dominios/${dominio.id}/registros`),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ['portal', 'dominios', dominio.id, 'registros'] });

  const salvar = useMutation({
    mutationFn: () =>
      api.put(`/portal/dominios/${dominio.id}/registros`, {
        nome: form!.nome,
        tipo: form!.tipo,
        ttl: form!.ttl,
        valores: form!.valores.split('\n').map((v) => v.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      void invalidar();
      setForm(null);
    },
  });

  const remover = useMutation({
    mutationFn: (r: Registro) =>
      api.delete(`/portal/dominios/${dominio.id}/registros?nome=${encodeURIComponent(r.nome)}&tipo=${r.tipo}`),
    onSuccess: () => void invalidar(),
  });

  const registros = data?.registros ?? [];

  return (
    <Modal aberto aoFechar={aoFechar} largura="largo" titulo={dominio.nome} descricao="Registros da zona">
      <div className="space-y-4">
        <SecaoDnssecPortal dominioId={dominio.id} />

        {podeEditar && !form && (
          <div className="flex justify-end">
            <Botao
              tamanho="tabela"
              onClick={() => setForm({ nome: '', tipo: 'A', ttl: 3600, valores: '' })}
              iconeEsquerda={<Plus size={13} />}
            >
              Novo registro
            </Botao>
          </div>
        )}

        {podeEditar && form && (
          <div className="space-y-3 rounded-card border border-marca/30 bg-tint/40 p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_120px_100px]">
              <label className="block">
                <Rotulo>Nome</Rotulo>
                <input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="@ (raiz) ou www"
                  className="h-10 w-full rounded-controle border border-borda px-3 font-mono text-[13px] focus:border-marca focus:outline-none"
                />
              </label>
              <label className="block">
                <Rotulo>Tipo</Rotulo>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="h-10 w-full rounded-controle border border-borda px-2 text-[13px] focus:border-marca focus:outline-none"
                >
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <Rotulo>TTL</Rotulo>
                <input
                  type="number"
                  value={form.ttl}
                  onChange={(e) => setForm({ ...form, ttl: Number(e.target.value) || 3600 })}
                  className="h-10 w-full rounded-controle border border-borda px-2 font-mono text-[13px] focus:border-marca focus:outline-none"
                />
              </label>
            </div>
            <label className="block">
              <Rotulo>Valor(es) — um por linha</Rotulo>
              <textarea
                value={form.valores}
                onChange={(e) => setForm({ ...form, valores: e.target.value })}
                rows={3}
                spellCheck={false}
                placeholder={dicaTipo(form.tipo)}
                className="w-full rounded-controle border border-borda px-3 py-2 font-mono text-[12.5px] focus:border-marca focus:outline-none"
              />
            </label>
            {salvar.error && (
              <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12px] font-semibold text-erro">
                {(salvar.error as ApiError).descricao}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Botao tamanho="tabela" onClick={() => setForm(null)}>
                Cancelar
              </Botao>
              <Botao
                tamanho="tabela"
                variante="primario"
                onClick={() => salvar.mutate()}
                carregando={salvar.isPending}
                disabled={!form.valores.trim()}
              >
                Salvar
              </Botao>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="h-32 animate-pulse rounded-chip bg-borda-tenue" />
        ) : (
          <div className="overflow-hidden rounded-chip border border-borda">
            <table className="w-full border-collapse text-[12.5px]">
              <tbody>
                {registros.map((r) => (
                  <tr key={`${r.nome}-${r.tipo}`} className="border-b border-borda-suave last:border-0">
                    <td className="px-3 py-2 align-top">
                      <Pill tom={r.sistema ? 'neutro' : 'info'}>{r.tipo}</Pill>
                    </td>
                    <td className="px-2 py-2 align-top font-mono text-texto-suave">{r.nome}</td>
                    <td className="px-2 py-2 align-top font-mono">
                      {r.valores.map((v, i) => (
                        <div key={i} className="break-all">
                          {v}
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      {podeEditar && !r.sistema && (
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() =>
                              setForm({ nome: r.nome, tipo: r.tipo, ttl: r.ttl, valores: r.valores.join('\n') })
                            }
                            className="text-[12px] font-semibold text-marca hover:underline"
                          >
                            editar
                          </button>
                          <button
                            onClick={() => remover.mutate(r)}
                            className="text-texto-suave hover:text-erro"
                            title="Remover"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-texto-suave">
          <AlertTriangle size={13} className="mt-px shrink-0" />
          {podeEditar
            ? 'Os registros valem no mundo só depois que os nameservers do domínio apontarem para os nossos nós. SOA e NS da raiz sustentam a zona e não podem ser removidos.'
            : 'Seu perfil consulta os registros, mas não os edita. Fale com o administrador da sua conta para alterar.'}
        </p>
      </div>
    </Modal>
  );
}

interface EstadoDnssec {
  assinada: boolean;
  ds: string[];
  dsPublicado: boolean;
  rollover: {
    status: string;
    keyTagVelho: number | null;
    keyTagNovo: number | null;
    dsNovo: string[];
  } | null;
}

/**
 * DNSSEC do domínio, no portal.
 *
 * O cliente vê o estado e, se assinado, rotaciona a chave — ele controla o pai
 * do próprio domínio (registro.br), então é ele quem publica o DS novo. Ativar e
 * desativar seguem com a Consult: por isso, se não estiver assinado, aqui só
 * aparece o aviso de que a ativação é interna.
 */
function SecaoDnssecPortal({ dominioId }: { dominioId: string }) {
  const qc = useQueryClient();
  const { pode } = useSessao();
  const podeRollover = pode('dominio:dnssec');
  const chave = ['portal', 'dominios', dominioId, 'dnssec'];

  const { data } = useQuery({
    queryKey: chave,
    queryFn: () => api.get<EstadoDnssec>(`/portal/dominios/${dominioId}/dnssec`),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: chave });
  const iniciar = useMutation({
    mutationFn: () => api.post(`/portal/dominios/${dominioId}/dnssec/rollover`),
    onSuccess: () => void invalidar(),
  });
  const verificar = useMutation({
    mutationFn: () =>
      api.post<{ detectadoNovo?: boolean }>(`/portal/dominios/${dominioId}/dnssec/rollover/verificar`),
    onSuccess: () => void invalidar(),
  });
  const cancelar = useMutation({
    mutationFn: () => api.delete(`/portal/dominios/${dominioId}/dnssec/rollover`),
    onSuccess: () => void invalidar(),
  });

  if (!data) return null;

  if (!data.assinada) {
    return (
      <div className="rounded-card border border-borda bg-fundo p-3 text-[12px] leading-snug text-texto-suave">
        <ShieldCheck size={14} className="mr-1 inline text-texto-mudo" />
        DNSSEC não está ativo neste domínio. A ativação é feita pela Consult — fale com o suporte.
      </div>
    );
  }

  return (
    <div className="rounded-card border border-borda bg-fundo p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-sucesso" />
        <span className="text-[13px] font-bold">DNSSEC</span>
        <Pill tom="ok">assinada</Pill>
      </div>

      {podeRollover ? (
        <SecaoRollover
          assinada={data.assinada}
          rollover={data.rollover}
          aoIniciar={() => iniciar.mutate()}
          iniciando={iniciar.isPending}
          aoVerificar={() => verificar.mutate()}
          verificando={verificar.isPending}
          aoCancelar={() => cancelar.mutate()}
          cancelando={cancelar.isPending}
          detectadoNovo={verificar.data?.detectadoNovo}
        />
      ) : (
        <p className="mt-2 text-[12px] text-texto-suave">
          Seu perfil não rotaciona a chave. Fale com o administrador da sua conta.
        </p>
      )}
    </div>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block text-[10.5px] font-bold uppercase tracking-[0.5px] text-texto-suave">
      {children}
    </span>
  );
}

function dicaTipo(tipo: string): string {
  switch (tipo) {
    case 'A':
      return '200.150.10.1';
    case 'AAAA':
      return '2001:db8::1';
    case 'CNAME':
      return 'destino.exemplo.com';
    case 'MX':
      return '10 mail.cliente.com.br';
    case 'TXT':
      return 'v=spf1 include:_spf.provedor.com ~all';
    case 'NS':
      return 'ns1.exemplo.com';
    case 'SRV':
      return '10 60 5060 sip.cliente.com.br';
    default:
      return '';
  }
}
